import { parseCpfList } from "../parseCpfList";
import { validateDateRange } from "./reportDateRange";
import {
  IInsightsReportParamSpec,
  IInsightsReportRequest,
} from "v2/services/insights.service";

/**
 * O estado dos controles de um card. Um objeto único para os três campos, e não
 * um por `kind`, porque o mesmo card pode declarar mais de um parâmetro.
 */
export interface IReportParamValues {
  desde: string;
  ate: string;
  cpfs: string;
}

export const EMPTY_PARAM_VALUES: IReportParamValues = {
  desde: "",
  ate: "",
  cpfs: "",
};

export interface IBuildReportRequestResult {
  /** `null` quando há erro — nada é enviado pela metade. */
  params: Omit<IInsightsReportRequest, "workspace_id"> | null;
  error: string | null;
  /**
   * Qual campo produziu o erro, para a mensagem aparecer nele em vez de solta
   * no rodapé do card. `undefined` quando não há erro.
   */
  errorField?: IInsightsReportParamSpec["kind"];
}

/**
 * Monta o payload a partir do que o relatório **declarou** pedir.
 *
 * Duas coisas de propósito:
 *
 * 1. **O que não foi declarado não é enviado.** Um relatório de período não
 *    recebe `cpfs` só porque o usuário digitou algo antes de trocar de card.
 * 2. **Isto não é a validação.** O backend revalida tudo em
 *    `validateReportParams`. O que se ganha aqui é dizer o problema na hora, em
 *    português, em vez de devolver um 400 genérico.
 */
export function buildReportRequest(
  specs: IInsightsReportParamSpec[],
  values: IReportParamValues,
  /** Injetável para teste; em produção é o dia local de quem está na tela. */
  today?: string
): IBuildReportRequestResult {
  const params: Omit<IInsightsReportRequest, "workspace_id"> = {};

  for (const spec of specs) {
    if (spec.kind === "intervalo-data") {
      const { desde, ate } = values;
      // Calendário real, nada no futuro, nada antes do início da operação e
      // teto de tamanho — o período vira `BETWEEN` numa query do Snowflake, e
      // um intervalo aberto demais não falha: responde, devagar e cobrando.
      const error = validateDateRange(desde, ate, today);
      if (error) return { params: null, error, errorField: spec.kind };
      params.desde = desde;
      params.ate = ate;
      continue;
    }

    if (spec.kind === "lista-cpf") {
      // O teto vem do spec, não de constante no front: se o backend publicar um
      // relatório com `max: 20`, um limite fixo aqui recusaria 11 CPFs válidos
      // dizendo "máximo de 10" — a tela mentindo sobre a regra do backend.
      const result = parseCpfList(values.cpfs, spec.max);
      if (result.error) {
        return { params: null, error: result.error, errorField: spec.kind };
      }
      params.cpfs = result.cpfs;
    }
  }

  return { params, error: null };
}

/**
 * Se o card já tem o suficiente para habilitar o botão. Só presença — o erro de
 * conteúdo aparece ao gerar, não a cada tecla digitada.
 */
export function hasRequiredParams(
  specs: IInsightsReportParamSpec[],
  values: IReportParamValues
): boolean {
  return specs.every((spec) => {
    if (spec.kind === "intervalo-data") {
      return Boolean(values.desde && values.ate);
    }
    if (spec.kind === "lista-cpf") {
      return Boolean(values.cpfs.trim());
    }
    return true;
  });
}
